import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogSurface,
  mergeClasses,
  Spinner,
  Text,
  Tooltip,
  makeStyles,
  shorthands,
  tokens,
} from '@coreai/fluentui-react';
import {
  DismissRegular,
} from '@fluentui/react-icons';
import { EditorState, Compartment, type Extension } from '@codemirror/state';
import {
  EditorView,
  keymap,
  lineNumbers,
  highlightActiveLine,
  tooltips,
} from '@codemirror/view';
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentWithTab,
} from '@codemirror/commands';
import { json } from '@codemirror/lang-json';
import {
  bracketMatching,
  foldGutter,
  foldKeymap,
  syntaxTree,
} from '@codemirror/language';
import {
  search,
  searchKeymap,
  highlightSelectionMatches,
} from '@codemirror/search';
import {
  closeBrackets,
  closeBracketsKeymap,
} from '@codemirror/autocomplete';
import { lintGutter, linter, type Diagnostic } from '@codemirror/lint';
import { useShallow } from 'zustand/react/shallow';
import { useAppStore } from '@/stores/appStore';
import { useNodeEditorStore } from '@/stores/nodeEditorStore';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import {
  selectIsPublishing,
  useDraftModeStore,
} from '@/stores/draftModeStore';
import { validateNodeCode } from '@/utils/workflow';
import {
  createFluentTheme,
  formatJsonKeymap,
} from '@/components/WorkflowDesigner/CodeEditor/extensions';

const useStyles = makeStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: tokens.spacingVerticalS,
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
  },
  toolbarMessage: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  guidance: {
    color: tokens.colorNeutralForeground2,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
  actions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  status: {
    color: tokens.colorPaletteDarkOrangeForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    '@media (forced-colors: active)': {
      color: 'CanvasText',
    },
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    '@media (forced-colors: active)': {
      color: 'LinkText',
    },
  },
  conflictRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
  editorContainer: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    borderRadius: tokens.borderRadiusMedium,
    ...shorthands.border('1px', 'solid', tokens.colorNeutralStroke1),
    overflow: 'hidden',
    '& .cm-editor': {
      flex: 1,
      width: '100%',
      height: '100%',
    },
    '& .cm-scroller': {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase300,
    },
  },
  dialogSurface: {
    width: 'min(1200px, 94vw)',
    maxWidth: '94vw',
    height: 'min(900px, 90vh)',
    maxHeight: '90vh',
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  },
  dialogBody: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    padding: 0,
    gap: 0,
    rowGap: 0,
  },
  dialogHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalL,
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    flexShrink: 0,
    flexWrap: 'wrap',
  },
  dialogTitle: {
    flex: '1 1 240px',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: tokens.fontSizeBase400,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  dialogEditorArea: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    gap: tokens.spacingVerticalS,
    paddingTop: tokens.spacingVerticalM,
    paddingRight: tokens.spacingHorizontalL,
    paddingBottom: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalL,
  },
  dialogEditorHost: {
    minHeight: 0,
  },
  dialogActions: {
    flexShrink: 1,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    maxWidth: '100%',
  },
  tooltipPortalHost: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: 0,
    height: 0,
    zIndex: 1100,
  },
});

export interface NodeCodeViewProps {
  /** The serialized node data to display. */
  value: unknown;
  /** Accessible label for the editor. */
  ariaLabel?: string;
}

const createNodeLinter = (): Extension =>
  linter(
    (view) => {
      const state = useNodeEditorStore.getState();
      const target = state.nodeCodeDraft.target;
      const expectedType = state.node?.nodeData.type;
      if (!target) return [];
      const text = view.state.doc.toString();
      const result = validateNodeCode(
        text,
        target,
        expectedType,
        state.node?.nodeData,
      );
      state.setNodeCodeDraftValidation(
        text,
        result.success ? null : result.error,
      );
      if (result.success) return [];
      const { from, to } = getDiagnosticRange(
        view.state,
        result.error,
      );
      return [
        {
          from,
          to,
          severity: 'error',
          message: result.error,
          source: 'node schema',
        } satisfies Diagnostic,
      ];
    },
    { delay: 300 },
  );

const getDiagnosticRange = (
  state: EditorState,
  message: string,
): { from: number; to: number } => {
  const documentLength = state.doc.length;
  const syntaxErrors: Array<{ from: number; to: number }> = [];
  syntaxTree(state).iterate({
    enter(node) {
      if (syntaxErrors.length === 0 && node.type.isError) {
        syntaxErrors.push({ from: node.from, to: node.to });
      }
    },
  });

  const syntaxError = syntaxErrors[0];
  if (syntaxError) {
    let { from, to } = syntaxError;
    if (from === to) {
      const current = state.doc.sliceString(from, Math.min(from + 1, documentLength));
      let previous = from - 1;
      while (
        previous >= 0 &&
        /\s/.test(state.doc.sliceString(previous, previous + 1))
      ) {
        previous--;
      }
      // JSON parsers report a trailing comma at the following `}`/`]`.
      // Point at the comma—the token the user actually needs to remove.
      if (
        (current === '}' || current === ']') &&
        previous >= 0 &&
        state.doc.sliceString(previous, previous + 1) === ','
      ) {
        return { from: previous, to: previous + 1 };
      }
      if (from < documentLength) {
        to = from + 1;
      } else if (previous >= 0) {
        from = previous;
        to = previous + 1;
      }
    }
    return { from, to };
  }

  // Schema errors name a JSON path. Highlight the deepest property that
  // exists, falling back through its parents when the missing field itself
  // has no token in the document.
  const path = /^([A-Za-z0-9_.-]+):/.exec(message)?.[1];
  if (path) {
    const content = state.doc.toString();
    const segments = path.split('.');
    for (let index = segments.length - 1; index >= 0; index--) {
      const segment = segments[index];
      if (!segment) continue;
      const key = `"${segment}"`;
      const keyIndex = content.indexOf(key);
      if (keyIndex >= 0) {
        return { from: keyIndex, to: keyIndex + key.length };
      }
    }
  }

  return {
    from: 0,
    to: Math.min(1, documentLength),
  };
};

/**
 * JSON code surface for the node editor's Code section. The existing
 * read-only behavior remains the default; the flagged edit mode uses an
 * explicit Apply transaction so form state and canvas state rehydrate once.
 */
export const NodeCodeView = ({
  value,
  ariaLabel = 'Node JSON',
}: NodeCodeViewProps) => {
  const styles = useStyles();
  const theme = useAppStore((state) => state.theme);
  const featureEnabled = useFeatureFlag('actionCodeEditing');
  const isPublishing = useDraftModeStore(selectIsPublishing);
  const {
    node,
    isReadOnly,
    nodeCodeDraft,
    prepareNodeCodeDraft,
    setNodeCodeDraftText,
    applyNodeCodeDraft,
    reloadNodeCodeDraft,
    isNodeCodeExpanded,
    setNodeCodeExpanded,
  } = useNodeEditorStore(
    useShallow((state) => ({
      node: state.node,
      isReadOnly: state.isReadOnly,
      nodeCodeDraft: state.nodeCodeDraft,
      prepareNodeCodeDraft: state.prepareNodeCodeDraft,
      setNodeCodeDraftText: state.setNodeCodeDraftText,
      applyNodeCodeDraft: state.applyNodeCodeDraft,
      reloadNodeCodeDraft: state.reloadNodeCodeDraft,
      isNodeCodeExpanded: state.isNodeCodeExpanded,
      setNodeCodeExpanded: state.setNodeCodeExpanded,
    })),
  );
  const editable =
    featureEnabled &&
    !isReadOnly &&
    !!node &&
    !(node.type === 'tool' && node.isMcpTool);
  const editorEditable =
    editable &&
    !isPublishing &&
    !nodeCodeDraft.isPreparing &&
    nodeCodeDraft.target !== null;

  const serializedValue = useMemo(() => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return String(value ?? '');
    }
  }, [value]);

  const inlineEditorRef = useRef<HTMLDivElement>(null);
  const dialogEditorRef = useRef<HTMLDivElement>(null);
  const viewRef = useRef<EditorView | null>(null);
  const themeCompartment = useRef(new Compartment());
  const editableCompartment = useRef(new Compartment());
  const lintCompartment = useRef(new Compartment());
  const ariaCompartment = useRef(new Compartment());
  const isUpdatingRef = useRef(false);
  const changeHandlerRef = useRef(setNodeCodeDraftText);
  changeHandlerRef.current = setNodeCodeDraftText;
  const [isApplying, setIsApplying] = useState(false);
  const dialogTitleId = `node-code-dialog-${useId()}`;

  useEffect(() => {
    if (!editable) return;
    void prepareNodeCodeDraft(serializedValue);
  }, [editable, node?.id, prepareNodeCodeDraft, serializedValue]);

  useEffect(() => {
    if (!inlineEditorRef.current) return;
    const fluentProvider =
      inlineEditorRef.current.closest<HTMLElement>('.fui-FluentProvider') ??
      document.body;
    const tooltipParent = document.createElement('div');
    tooltipParent.className = styles.tooltipPortalHost;
    fluentProvider.appendChild(tooltipParent);

    const extensions: Extension[] = [
      lineNumbers(),
      history(),
      foldGutter({ openText: '▼', closedText: '▶' }),
      bracketMatching(),
      closeBrackets(),
      highlightActiveLine(),
      highlightSelectionMatches(),
      search({ top: true }),
      json(),
      EditorState.tabSize.of(2),
      ariaCompartment.current.of(
        EditorView.contentAttributes.of({
          'aria-label': ariaLabel,
          'aria-readonly': String(!editorEditable),
        }),
      ),
      keymap.of([
        ...closeBracketsKeymap,
        ...defaultKeymap,
        ...historyKeymap,
        ...foldKeymap,
        ...searchKeymap,
        ...formatJsonKeymap,
        indentWithTab,
      ]),
      themeCompartment.current.of(createFluentTheme(theme)),
      editableCompartment.current.of([
        EditorState.readOnly.of(!editorEditable),
        EditorView.editable.of(true),
      ]),
      lintCompartment.current.of(
        editorEditable ? [createNodeLinter(), lintGutter()] : [],
      ),
      // Escape the transformed drawer (which clips fixed descendants) while
      // staying inside FluentProvider so theme CSS variables still resolve.
      tooltips({ parent: tooltipParent }),
      EditorView.updateListener.of((update) => {
        if (update.docChanged && !isUpdatingRef.current) {
          changeHandlerRef.current(update.state.doc.toString());
        }
      }),
    ];

    const initialDocument =
      editorEditable && nodeCodeDraft.target
        ? nodeCodeDraft.text
        : serializedValue;
    const state = EditorState.create({
      doc: initialDocument,
      extensions,
    });
    const view = new EditorView({ state, parent: inlineEditorRef.current });
    viewRef.current = view;
    view.focus();

    return () => {
      view.destroy();
      viewRef.current = null;
      tooltipParent.remove();
    };
    // CodeMirror is reconfigured for theme/editability below. Node changes
    // rebuild the editor so undo history never crosses definition targets.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ariaLabel, node?.id]);

  useLayoutEffect(() => {
    const view = viewRef.current;
    const host = isNodeCodeExpanded
      ? dialogEditorRef.current
      : inlineEditorRef.current;
    if (!view || !host || view.dom.parentElement === host) return;
    host.appendChild(view.dom);
    view.requestMeasure();
    view.focus();
  }, [isNodeCodeExpanded]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: themeCompartment.current.reconfigure(createFluentTheme(theme)),
    });
  }, [theme]);

  useEffect(() => {
    const view = viewRef.current;
    if (!view) return;
    view.dispatch({
      effects: [
        editableCompartment.current.reconfigure([
          EditorState.readOnly.of(!editorEditable),
          EditorView.editable.of(true),
        ]),
        lintCompartment.current.reconfigure(
          editorEditable ? [createNodeLinter(), lintGutter()] : [],
        ),
        ariaCompartment.current.reconfigure(
          EditorView.contentAttributes.of({
            'aria-label': ariaLabel,
            'aria-readonly': String(!editorEditable),
          }),
        ),
      ],
    });
  }, [ariaLabel, editorEditable]);

  const currentDocument =
    editable && nodeCodeDraft.target
      ? nodeCodeDraft.text
      : serializedValue;
  useEffect(() => {
    const view = viewRef.current;
    if (!view || view.state.doc.toString() === currentDocument) return;
    const scrollTop = view.scrollDOM.scrollTop;
    const scrollLeft = view.scrollDOM.scrollLeft;
    const selection = view.state.selection.main;
    const documentLength = currentDocument.length;
    isUpdatingRef.current = true;
    view.dispatch({
      changes: {
        from: 0,
        to: view.state.doc.length,
        insert: currentDocument,
      },
      selection: {
        anchor: Math.min(selection.anchor, documentLength),
        head: Math.min(selection.head, documentLength),
      },
      scrollIntoView: false,
    });
    isUpdatingRef.current = false;
    view.scrollDOM.scrollTop = scrollTop;
    view.scrollDOM.scrollLeft = scrollLeft;
  }, [currentDocument]);

  const handleApply = useCallback(async () => {
    setIsApplying(true);
    try {
      await applyNodeCodeDraft();
    } finally {
      setIsApplying(false);
    }
  }, [applyNodeCodeDraft]);

  const handleRevert = useCallback(() => {
    void reloadNodeCodeDraft();
  }, [reloadNodeCodeDraft]);

  const blockingError =
    nodeCodeDraft.validationError ?? nodeCodeDraft.conflictError;
  const revertDisabled =
    isApplying ||
    isPublishing ||
    nodeCodeDraft.isPreparing ||
    (!nodeCodeDraft.isDirty && !nodeCodeDraft.conflictError);
  const applyDisabled =
    isApplying ||
    isPublishing ||
    nodeCodeDraft.isPreparing ||
    nodeCodeDraft.isValidationPending ||
    !nodeCodeDraft.isDirty ||
    !!blockingError;

  return (
    <div className={styles.root}>
      {editable && (
        <>
          <div className={styles.toolbar}>
            {nodeCodeDraft.isDirty ? (
              <Text
                className={mergeClasses(styles.toolbarMessage, styles.status)}
                role="status"
                aria-live="polite"
              >
                Unapplied changes
              </Text>
            ) : (
              <Text
                className={mergeClasses(styles.toolbarMessage, styles.guidance)}
              >
                Edit this node&apos;s runtime JSON, then apply it to update the workflow.
              </Text>
            )}
            <div className={styles.actions}>
              <Button
                appearance="secondary"
                size="small"
                onClick={handleRevert}
                disabled={revertDisabled}
              >
                Revert
              </Button>
              <Button
                appearance="primary"
                size="small"
                onClick={handleApply}
                disabled={applyDisabled}
              >
                {isApplying ? <Spinner size="tiny" /> : null}
                {isApplying ? 'Applying' : 'Apply changes'}
              </Button>
            </div>
          </div>
          {nodeCodeDraft.validationError && (
            <Text className={styles.error} role="alert">
              {nodeCodeDraft.validationError}
            </Text>
          )}
          {nodeCodeDraft.conflictError && (
            <div className={styles.conflictRow} role="alert">
              <Text className={styles.error}>
                {nodeCodeDraft.conflictError}
              </Text>
              <Button
                appearance="secondary"
                size="small"
                onClick={handleRevert}
              >
                Reload latest
              </Button>
            </div>
          )}
        </>
      )}
      <div ref={inlineEditorRef} className={styles.editorContainer} />
      <Dialog
        open={isNodeCodeExpanded}
        onOpenChange={(_, data) => {
          if (!data.open) setNodeCodeExpanded(false);
        }}
        modalType="modal"
      >
        <DialogSurface
          className={styles.dialogSurface}
          aria-labelledby={dialogTitleId}
          onKeyDown={(event) => {
            if (event.key === 'Escape') event.stopPropagation();
          }}
        >
          <DialogBody className={styles.dialogBody}>
            <div className={styles.dialogHeader}>
              <Text id={dialogTitleId} as="h2" className={styles.dialogTitle}>
                Code — {node?.name ?? 'Node'}
              </Text>
              <div
                className={mergeClasses(
                  styles.actions,
                  styles.dialogActions,
                )}
              >
                {nodeCodeDraft.isDirty && (
                  <Text
                    className={styles.status}
                    role="status"
                    aria-live="polite"
                  >
                    Unapplied changes
                  </Text>
                )}
                <Button
                  appearance="secondary"
                  size="small"
                  onClick={handleRevert}
                  disabled={revertDisabled}
                >
                  Revert
                </Button>
                <Button
                  appearance="primary"
                  size="small"
                  onClick={handleApply}
                  disabled={applyDisabled}
                >
                  {isApplying ? <Spinner size="tiny" /> : null}
                  {isApplying ? 'Applying' : 'Apply changes'}
                </Button>
                <Tooltip content="Close" relationship="label">
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<DismissRegular />}
                    onClick={() => setNodeCodeExpanded(false)}
                    aria-label="Close expanded code editor"
                  />
                </Tooltip>
              </div>
            </div>
            <div className={styles.dialogEditorArea}>
              {nodeCodeDraft.validationError && (
                <Text className={styles.error} role="alert">
                  {nodeCodeDraft.validationError}
                </Text>
              )}
              {nodeCodeDraft.conflictError && (
                <div className={styles.conflictRow} role="alert">
                  <Text className={styles.error}>
                    {nodeCodeDraft.conflictError}
                  </Text>
                  <Button
                    appearance="secondary"
                    size="small"
                    onClick={handleRevert}
                  >
                    Reload latest
                  </Button>
                </div>
              )}
              <div
                ref={dialogEditorRef}
                className={mergeClasses(
                  styles.editorContainer,
                  styles.dialogEditorHost,
                )}
              />
            </div>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
};
